import React, { useRef, FC } from "react";
import { Canvas, useFrame, extend, useThree } from "react-three-fiber";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Horn } from "./Horn";
import { IHornDesignResponse } from "../../interfaces";
import { Link } from "react-router-dom";
import { Box, makeStyles, Tooltip, Typography } from "@material-ui/core";
import colors from "../../constants/colors";
import { observer } from "mobx-react-lite";
import { faqs } from "../FaqAccordion";
import Info from "@material-ui/icons/Info";
import { useVisibility } from "../../utils/useVisibility";
import { store } from "../../store";
import { toFixedInches } from "../../utils/toFixedInches";

extend({ OrbitControls });
const Controls = (props) => {
  const { gl, camera } = useThree();
  const ref = useRef(null);
  useFrame(() => ref?.current?.update());
  return (
    // @ts-ignore
    <orbitControls
      autoRotate={props.rotate}
      ref={ref}
      args={[camera, gl.domElement]}
      {...props}
    />
  );
};

const getCameraDistance = (
  maxWidth,
  maxHeight,
  maxLength,
  K = 1,
  angle = 50
) => {
  const R = Math.sqrt(Math.pow(maxWidth, 2) + Math.pow(maxHeight, 2));
  return (K * R) / Math.tan(angle) + maxLength;
};

interface IModelProps {
  output: IHornDesignResponse;
  driverName?: string;
  rotate?: boolean;
  small?: boolean;
  geometry?: number;
}

const Model: FC<IModelProps> = observer(
  ({ output, rotate = false, small = false, driverName, geometry }) => {
    const [isVisible, currentElement] = useVisibility<HTMLDivElement>(800);
    const classes = styles();
    const maxWidth = output.hWidth[output.hWidth.length - 1];
    const maxHeight = output.hHeight[output.hHeight.length - 1];
    const maxLength = output.hXval[output.hXval.length - 1];
    const cameraDistance = getCameraDistance(maxWidth, maxHeight, maxLength);
    const { isImperial } = store;
    const height = isImperial
      ? toFixedInches(maxHeight * 2 * 1000) + '"'
      : Math.round(maxHeight * 2 * 1000) + "mm";
    const width = isImperial
      ? toFixedInches(maxWidth * 2 * 1000) + '"'
      : Math.round(maxWidth * 2 * 1000) + "mm";
    const length = isImperial
      ? toFixedInches(maxLength * 1000) + '"'
      : Math.round(maxLength * 1000) + "mm";

    return (
      // @ts-ignore
      <Box ref={currentElement} className={classes.container}>
        <Box height={small ? 200 : 400}>
          {isVisible && (
            <Canvas
              style={{
                height: small ? 200 : 400,
                backgroundColor: colors.grey,
                borderRadius: 15,
                width: "100%",
              }}
              camera={{
                position: [-1 * cameraDistance, 0, (-1 * cameraDistance) / 2],
                near: 0.005,
                fov: 50,
              }}
            >
              {!small && <Controls rotate={rotate} />}
              <ambientLight intensity={1} />
              <pointLight
                intensity={5}
                position={[-80, -10, -10]}
                color="#ff0000"
              />
              <pointLight
                intensity={5}
                position={[80, 10, 10]}
                color="#ff0000"
              />
              <spotLight
                castShadow
                intensity={1}
                angle={Math.PI / 8}
                position={[15, 25, 5]}
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
              />
              <Horn landing={rotate} output={output} geometry={geometry} />
              {/* <ScreenshotButton /> */}
            </Canvas>
          )}
        </Box>

        {!(rotate || small) && (
          <>
            <Box
              bottom={0}
              position={["relative", "absolute"]}
              p={1}
              m={1}
              borderRadius={15}
              display={["flex", "block"]}
              bgcolor="white"
              flexWrap="wrap"
              justifyContent="space-around"
            >
              <Typography>Height: {height}</Typography>
              <Typography>Width: {width}</Typography>
              <Typography>Length: {length}</Typography>
              {driverName && (
                <Typography>
                  Driver:{" "}
                  <Box component="span" whiteSpace={["nowrap", "normal"]}>
                    {driverName}
                  </Box>
                </Typography>
              )}
            </Box>
            <Box top={0} right={0} position="absolute" p={1}>
              <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={faqs.find(({ id }) => id === "viewHorn").answer}
              >
                <Link style={{ color: "inherit" }} to="/faq">
                  <Info />
                </Link>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>
    );
  }
);

export default Model;

const styles = makeStyles(() => ({
  container: {
    position: "relative",
    width: "calc(100% - 1em)",
  },
  tooltip: { fontSize: 14 },
}));
